import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { categories } from '../../mocks/categories';

export default function HomePage() {
  useEffect(() => {
    document.title = 'Task Zone – Servicios Confiables en Aguascalientes | Plomería, Electricidad y Más';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Task Zone conecta usuarios con proveedores verificados de servicios en Aguascalientes: plomería, electricidad, limpieza, carpintería y más. Contrata de forma segura, transparente y rápida con calificaciones reales y precios en MXN.');

    const schemaId = 'schema-home';
    if (!document.getElementById(schemaId)) {
      const script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      script.text = JSON.stringify([
        {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'Task Zone',
          url: import.meta.env.VITE_SITE_URL || 'https://example.com',
          description: 'Plataforma digital que conecta usuarios con proveedores verificados de servicios en Aguascalientes, México.',
          potentialAction: {
            '@type': 'SearchAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/explorar?categoria={search_term_string}`,
            },
            'query-input': 'required name=search_term_string',
          },
        },
        {
          '@context': 'https://schema.org',
          '@type': 'LocalBusiness',
          name: 'Task Zone',
          description: 'Plataforma de contratación de servicios verificados en Aguascalientes: plomería, electricidad, limpieza, carpintería y más.',
          url: import.meta.env.VITE_SITE_URL || 'https://example.com',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Aguascalientes',
            addressRegion: 'AGU',
            addressCountry: 'MX',
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: 21.8818,
            longitude: -102.2916,
          },
          areaServed: {
            '@type': 'City',
            name: 'Aguascalientes',
          },
          priceRange: '$',
          currenciesAccepted: 'MXN',
          sameAs: [],
        },
      ]);
      document.head.appendChild(script);
    }

    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  const features = [
    {
      icon: 'ri-shield-user-line',
      title: 'Seguridad y confianza',
      description: 'Proveedores verificados con calificaciones y reseñas auténticas.'
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Agendamiento flexible',
      description: 'Facilidad para programar servicios en el horario que mejor convenga.'
    },
    {
      icon: 'ri-time-line',
      title: 'Ahorro de tiempo',
      description: 'Elimina la necesidad de buscar en múltiples lugares o pedir recomendaciones informales.'
    },
    {
      icon: 'ri-briefcase-4-line',
      title: 'Flexibilidad laboral',
      description: 'Oportunidad de ofrecer servicios en sus tiempos libres o complementar ingresos.'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/ccb427c0c22bef30316940ebca6a6c79.jpg"
            alt="Hero Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            Servicios confiables a tu alcance
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-4 max-w-3xl mx-auto">
            Rápido, seguro y sin complicaciones.
          </p>
          <p className="text-lg md:text-xl text-white/80 mb-10 max-w-3xl mx-auto">
            Encuentra profesionales calificados o regístrate para ofrecer tus servicios y llegar a más clientes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/explorar"
              className="px-8 py-4 bg-orange-600 text-white text-lg font-semibold rounded-lg hover:bg-orange-700 transition-all cursor-pointer whitespace-nowrap inline-flex items-center justify-center"
            >
              Explorar Servicios
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
            <Link
              to="/registro-proveedor"
              className="px-8 py-4 bg-white text-orange-600 text-lg font-semibold rounded-lg hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap inline-flex items-center justify-center"
            >
              Ser Proveedor
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">¿Por qué elegir Task Zone!?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              La plataforma más confiable para encontrar profesionales del hogar en Aguascalientes
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center p-6 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all">
                <div className="w-16 h-16 flex items-center justify-center bg-orange-100 rounded-full mx-auto mb-4">
                  <i className={`${feature.icon} text-3xl text-orange-600`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Servicios Populares</h2>
            <p className="text-lg text-gray-600">
              Encuentra el profesional que necesitas en minutos
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.slice(0, 8).map((category) => (
              <Link
                key={category.id}
                to={`/explorar?categoria=${encodeURIComponent(category.name)}`}
                className="bg-white p-6 rounded-xl hover:shadow-lg transition-all cursor-pointer group"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-orange-100 rounded-lg mb-4 group-hover:bg-orange-600 transition-all">
                  <i className={`${category.icon} text-2xl text-orange-600 group-hover:text-white transition-all`}></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{category.name}</h3>
                <p className="text-sm text-gray-600 mb-3">{category.description}</p>
                <p className="text-sm text-orange-600 font-medium">
                  {category.providerCount} proveedores disponibles
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Cómo Funciona</h2>
            <p className="text-lg text-gray-600">
              Tres simples pasos para obtener el servicio que necesitas
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-orange-600 text-white text-3xl font-bold rounded-full mx-auto mb-6">
                1
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Busca el Servicio</h3>
              <p className="text-gray-600">
                Explora nuestras categorías y encuentra el profesional que necesitas
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-orange-600 text-white text-3xl font-bold rounded-full mx-auto mb-6">
                2
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Solicita el Servicio</h3>
              <p className="text-gray-600">
                Completa el formulario con los detalles de tu solicitud
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-orange-600 text-white text-3xl font-bold rounded-full mx-auto mb-6">
                3
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Recibe el Servicio</h3>
              <p className="text-gray-600">
                El proveedor se pondrá en contacto contigo para confirmar los detalles
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-orange-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            ¿Eres un Profesional de Servicios?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Únete a nuestra plataforma y conecta con clientes que necesitan tus servicios
          </p>
          <Link
            to="/registro-proveedor"
            className="inline-flex items-center justify-center px-8 py-4 bg-white text-orange-600 text-lg font-semibold rounded-lg hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap"
          >
            Registrarse como Proveedor
            <i className="ri-arrow-right-line ml-2"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}